/**
 * Copyright ©2022 Drivepoint
 */

import React, {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import {Button} from "@mui/material";
import {useLocation, useNavigate} from "react-router-dom";
import useStateChange from "@hooks/useStateChange";
import {BreadcrumbItem} from "@services/BreadcrumbManager";
import ServiceRegistry from "@services/ServiceRegistry";
import "./Breadcrumbs.less";

export type BreadcrumbBarProps = {
};

export type BreadcrumbBarInterface = {
  setPageLabel: (id: string, label: string) => void;
  setTrailingCrumb: (crumb: BreadcrumbItem | null) => void;
};

const Breadcrumbs = forwardRef<BreadcrumbBarInterface, BreadcrumbBarProps>((props, ref): any => {

  useImperativeHandle(ref, (): BreadcrumbBarInterface => ({
    setPageLabel: (id: string, label: string): void => setPageLabel(id, label),
    setTrailingCrumb: (crumb: BreadcrumbItem | null): void => setTrailingCrumb(crumb)
  }));

  const navigate = useNavigate();
  const location = useLocation();
  const company = useStateChange<any>("company");
  const page = ServiceRegistry.routeService.getPageByPathname(location.pathname);

  const [pageLabels, setPageLabels] = useState<any>({});
  const [pathParams, setPathParams] = useState<Record<string, string>>({});
  const [trailingCrumb, setTrailingCrumb] = useState<BreadcrumbItem | null>(null);

  useEffect(() => {
    setTrailingCrumb(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!page) { return; }
    const match = location.pathname.match(page.pattern);
    if (!match) { return; }
    setPathParams(match
      .slice(1)
      .reduce((paramsObj: Record<string, string>, item, index) => {
        if (page.tokens?.[index]) { paramsObj[page.tokens[index].replace(":", "")] = item; }
        return paramsObj;
      }, {}));
  }, [page]);

  function setPageLabel(id: string, label: string): void {
    setPageLabels({...pageLabels, [id]: label});
  }

  function generatePath(item: BreadcrumbItem): string | undefined {
    const page = item.pageId === "" ? ServiceRegistry.routeService.defaultPage : ServiceRegistry.routeService.getPageById(item.pageId);
    if (!page) { return; }
    return page.path.replace(/:(\w+)/g, (match: any, group: any) => `${pathParams[group]}`);
  }

  function onBreadcrumbClick(item: BreadcrumbItem, event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    const path = generatePath(item);
    if (path) {
      if (event.metaKey || event.ctrlKey) { window.open(path, "_blank"); } else { navigate(path); }

    }
  }

  function renderBreadcrumb(item: BreadcrumbItem, index: number, current: boolean): any {
    const label = pageLabels?.[item.id] ?? item.label;
    return <div key={`crumb_${index}`} className="breadcrumbs-breadcrumb">
      {!!index && <div className="breadcrumbs-breadcrumb-separator">/</div>}
      <Button
        className="breadcrumbs-breadcrumb-button"
        disabled={current}
        disableRipple
        onClick={(event) => onBreadcrumbClick(item, event)}>{label}</Button>
    </div>;
  }

  function walkParentPages(page: any): any[] {
    if (!page?.parent) { return [page].filter(it => it); }
    return [
      page,
      walkParentPages(ServiceRegistry.routeService.getPageById(page.parent))
    ]
      .filter(it => it)
      .flat();
  }

  function renderBreadcrumbs(): any {
    const pages = walkParentPages(page);
    if (company?.name) { pages.push({id: "", name: company.name, pageId: ""}); }
    const crumbs: BreadcrumbItem[] = pages.reverse().map(page => ({id: page.id, label: page.name, pageId: page.id}));
    if (trailingCrumb) { crumbs.push(trailingCrumb); }
    return <div className="breadcrumbs-breadcrumbs">
      {crumbs.map((item: BreadcrumbItem, index: number) => renderBreadcrumb(item, index, index === crumbs.length - 1))}
    </div>;
  }
  return <div className="breadcrumbs">
    {renderBreadcrumbs()}
  </div>;

});

export default Breadcrumbs;
